import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Header from '../../components/ui/Header';
import Footer from '../homepage/components/Footer';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ViewerCanvas from '../3d-model-viewer/components/ViewerCanvas';
import ViewerControls from '../3d-model-viewer/components/ViewerControls';
import { useAuth } from '../../contexts/AuthContext';
import { userPaymentMethodService } from '../../services/userPaymentMethodService';

export default function DiagnosticReport() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const assemblyId = searchParams.get('assemblyId');
  const mode = searchParams.get('mode'); // 'preview'
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Loaded state
  const [assembly, setAssembly] = useState(null);
  const [allRecords, setAllRecords] = useState([]);
  const [shopAccount, setShopAccount] = useState(null);
  const [componentWraps, setComponentWraps] = useState({});
  const [componentWrapsBefore, setComponentWrapsBefore] = useState({});
  const [showBeforeWrap, setShowBeforeWrap] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paying, setPaying] = useState(false);

  // Payment method selection & saved cards
  const [userPaymentMethods, setUserPaymentMethods] = useState([]);
  const [selectedPaymentType, setSelectedPaymentType] = useState('card_on_file'); // 'card_on_file' | 'invoice_card'

  // Viewer states
  const [selectedComponent, setSelectedComponent] = useState(null);
  const [chunkProgress, setChunkProgress] = useState(0);
  const [showModelInfo, setShowModelInfo] = useState(true);
  const [viewMode, setViewMode] = useState(mode === 'preview' ? 'shaded' : 'x-ray-gray');
  const [lighting, setLighting] = useState({
    intensity: 75,
    temperature: 5500,
    shadowSoftness: 50,
    environment: 30,
    ambientOcclusion: 40,
    preset: 'studio',
    showEnvironmentBackground: false,
  });

  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showGrid, setShowGrid] = useState(false);
  const [showLighting, setShowLighting] = useState(false);
  const [isInfiniteVertical, setIsInfiniteVertical] = useState(false);
  const [isExploded, setIsExploded] = useState(false);
  const [explodeScaleX, setExplodeScaleX] = useState(1.0);
  const [explodeScaleY, setExplodeScaleY] = useState(1.0);
  const [explodeScaleZ, setExplodeScaleZ] = useState(1.0);
  const [isExplodeLocked, setIsExplodeLocked] = useState(false);

  const canvasRef = useRef();

  // Load diagnostic report data symmetrically
  useEffect(() => {
    if (!assemblyId) {
      setError('Report access code (assemblyId) is missing or invalid.');
      setLoading(false);
      return;
    }

    const loadReportData = async () => {
      try {
        setLoading(true);
        setError('');

        if (mode === 'preview') {
          // Fetch custom wrap configurator livery instead
          const { data: configData, error: configErr } = await supabase
            .from('custom_wrap_configs')
            .select(`
              *,
              product:products(
                id, name, description, gltf_file_path, thumbnail_path
              )
            `)
            .eq('id', assemblyId)
            .maybeSingle();

          if (configErr) throw configErr;
          if (!configData) {
            setError('This custom wrap configuration or livery draft could not be found.');
            setLoading(false);
            return;
          }

          if (configData.product?.gltf_file_path) {
            const rawPath = configData.product.gltf_file_path;
            if (rawPath.startsWith('http')) {
              configData.product.modelUrl = rawPath;
            } else {
              const { data: storageUrl } = supabase.storage.from('product-3d-models').getPublicUrl(rawPath);
              configData.product.modelUrl = storageUrl?.publicUrl || '';
            }
          }

          let customerName = 'Valued Client';
          if (configData.assembly_id) {
            try {
              const { data: assData } = await supabase
                .from('customer_assemblies')
                .select(`
                  id,
                  customer:shop_customers(name)
                `)
                .eq('id', configData.assembly_id)
                .maybeSingle();
              if (assData?.customer?.name) {
                customerName = assData.customer.name;
              }
            } catch (err) {
              console.warn('Could not fetch assembly customer:', err);
            }
          }

          setAssembly({
            id: configData.id,
            product: configData.product,
            nickname: configData.config_name,
            customer: { name: customerName }
          });
          if (configData.config_data) {
            const config = configData.config_data;
            if (config.before || config.after) {
              setComponentWraps(config.after || {});
              setComponentWrapsBefore(config.before || {});
            } else {
              setComponentWraps(config);
              setComponentWrapsBefore({});
            }
          }
          setServiceRecords([]);

          if (configData.shop_id) {
            const { data: shopData } = await supabase
              .from('seller_accounts')
              .select('id, business_name, business_slug, rating')
              .eq('id', configData.shop_id)
              .maybeSingle();
            if (shopData) setShopAccount(shopData);
          }
          return;
        }

        // 1. Fetch assembly details
        const { data: assData, error: assErr } = await supabase
          .from('customer_assemblies')
          .select(`
            *,
            customer:shop_customers(
              id, name, email, phone, shop_id
            ),
            product:products(
              id, name, description, gltf_file_path, thumbnail_path
            )
          `)
          .eq('id', assemblyId)
          .maybeSingle();

        if (assErr) throw assErr;
        if (!assData) {
          setError('This diagnostic report or vehicle asset could not be found.');
          setLoading(false);
          return;
        }

        // Map relative storage URL path
        if (assData.product?.gltf_file_path) {
          const rawPath = assData.product.gltf_file_path;
          if (rawPath.startsWith('http')) {
            assData.product.modelUrl = rawPath;
          } else {
            const { data: storageUrl } = supabase.storage.from('product-3d-models').getPublicUrl(rawPath);
            assData.product.modelUrl = storageUrl?.publicUrl || '';
          }
        }
        setAssembly(assData);

        // 2. Fetch all service ledger records for this assembly
        const { data: recordsData, error: recsErr } = await supabase
          .from('assembly_service_records')
          .select('*')
          .eq('assembly_id', assemblyId)
          .order('date_serviced', { ascending: false });

        if (recsErr) {
          console.error('Error fetching service records:', recsErr);
        }
        if (recordsData) setAllRecords(recordsData);

        // 3. Fetch shop/seller details
        if (assData.customer?.shop_id) {
          const { data: shopData } = await supabase
            .from('seller_accounts')
            .select('id, business_name, business_slug, rating')
            .eq('id', assData.customer.shop_id)
            .maybeSingle();
          if (shopData) setShopAccount(shopData);
        }

      } catch (err) {
        console.error('Error loading diagnostic report:', err);
        setError('Failed to fetch interactive diagnostic logs. Please verify link integrity.');
      } finally {
        setLoading(false);
      }
    };

    loadReportData();
  }, [assemblyId]);

  const invoiceNo = searchParams.get('invoiceNo');

  // Bulletproof client-side filtering matching both categorized and uncategorized work orders
  const serviceRecords = useMemo(() => {
    if (!invoiceNo) return allRecords;

    const isUncategorized = (inv) => {
      if (!inv) return true;
      const s = String(inv).trim().toLowerCase();
      return s === '' || s === 'null' || s === 'undefined' || s.includes('general') || s.includes('uncategorized') || s.includes('unscheduled');
    };

    const targetIsUncategorized = isUncategorized(invoiceNo);

    return allRecords.filter(r => {
      const recInvoice = r.invoice_number?.trim();
      if (targetIsUncategorized) {
        return isUncategorized(recInvoice);
      }
      return recInvoice?.toLowerCase() === invoiceNo.trim().toLowerCase();
    });
  }, [allRecords, invoiceNo]);

  // Total invoice cost calculation based on active approved records
  const totalInvoiceCost = useMemo(() => {
    return (serviceRecords || [])
      .filter(r => r.client_approved !== false)
      .reduce((sum, r) => sum + (parseFloat(r.cost) || 0), 0);
  }, [serviceRecords]);

  // Load user saved payment methods for invoice payment
  useEffect(() => {
    if (user?.id) {
      userPaymentMethodService.getUserPaymentMethods(user.id).then(methods => {
        if (methods && methods.length > 0) {
          setUserPaymentMethods(methods);
        }
      }).catch(err => console.warn('Could not load user payment methods:', err));
    }
  }, [user?.id]);

  const componentColors = useMemo(() => ({}), []);
  const handleMaterialColorChange = () => {};

  const handleReset = () => {
    setViewMode(mode === 'preview' ? 'shaded' : 'x-ray-gray');
    setLighting({
      intensity: 75,
      temperature: 5500,
      shadowSoftness: 50,
      environment: 30,
      ambientOcclusion: 40,
      preset: 'studio',
      showEnvironmentBackground: false,
    });
    setShowGrid(false);
    setIsPlaying(false);
    setIsExploded(false);
    setExplodeScaleX(1.0);
    setExplodeScaleY(1.0);
    setExplodeScaleZ(1.0);
    setIsExplodeLocked(false);
    canvasRef.current?.resetCamera();
  };

  const handleScreenshot = () => {
    alert('Symmetrical Screenshot: Project state captured and saved to your device!');
  };

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    alert('Shareable report link copied to clipboard!');
  };

  const handleScaleChangeX = (val) => setExplodeScaleX(val);
  const handleScaleChangeY = (val) => setExplodeScaleY(val);
  const handleScaleChangeZ = (val) => setExplodeScaleZ(val);
  const handleToggleExplodeLock = () => setIsExplodeLocked(p => !p);

  // Group service entries by Invoice Number
  const groupedInvoices = useMemo(() => {
    const grouped = {};
    serviceRecords.forEach(r => {
      const invNum = r.invoice_number?.trim() || 'General / Uncategorized Work';
      if (!grouped[invNum]) grouped[invNum] = [];
      grouped[invNum].push(r);
    });
    return grouped;
  }, [serviceRecords]);

  // Map service record array to format compatible with ViewerCanvas for mesh highlights!
  const diagnosticMap = useMemo(() => {
    const map = {};
    serviceRecords.forEach(r => {
      if (r.component_id) {
        map[r.component_id] = r.status; // maps part name to status (replaced, repaired, etc)
      }
    });
    return map;
  }, [serviceRecords]);

  const handleComponentSelect = (comp) => {
    setSelectedComponent(comp);
  };

  const handleToggleApproval = async (id, currentVal) => {
    try {
      const newVal = !currentVal;
      const { error } = await supabase
        .from('assembly_service_records')
        .update({ client_approved: newVal })
        .eq('id', id);
      if (error) throw error;
      
      // Symmetrically update local state to refresh running sums
      setAllRecords(prev => prev.map(r => r.id === id ? { ...r, client_approved: newVal } : r));
    } catch (err) {
      console.error('Error toggling client approval:', err);
    }
  };

  const handlePayInvoice = async () => {
    try {
      setPaying(true);
      
      // Update status of all records under this invoice assembly to 'completed' / PAID
      const { error } = await supabase
        .from('assembly_service_records')
        .update({ status: 'completed' })
        .eq('assembly_id', assemblyId);
      if (error) throw error;

      setAllRecords(prev => prev.map(r => ({ ...r, status: 'completed' })));
      setShowPaymentModal(false);
      alert('Symmetrical Invoice Stripe Checkout: Payment Completed successfully!');
    } catch (err) {
      console.error('Error completing payment:', err);
      alert('Failed to complete payment: ' + err.message);
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-between bg-background">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center p-6 text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mb-4" />
          <h2 className="text-lg font-bold animate-pulse uppercase tracking-wider">
            Compiling Interactive 3D Diagnostics...
          </h2>
          <p className="text-xs text-muted-foreground mt-2 max-w-sm leading-relaxed">
            Assembling geometric parts data, mapping work invoices, and parsing diagnostic highlights on-the-fly.
          </p>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !assembly) {
    return (
      <div className="min-h-screen flex flex-col justify-between bg-background">
        <Header />
        <main className="flex-1 flex flex-col items-center justify-center p-6 text-center max-w-md mx-auto">
          <div className="w-12 h-12 bg-red-100 text-red-600 rounded-full flex items-center justify-center mb-4">
            <Icon name="AlertTriangle" size={24} />
          </div>
          <h2 className="text-lg font-bold mb-2">Access Denied / Not Found</h2>
          <p className="text-sm text-muted-foreground mb-6 leading-relaxed">{error || 'This report is unavailable.'}</p>
          <Button variant="default" onClick={() => navigate('/model-catalog')} className="w-full">
            Return to Public Catalog
          </Button>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-background overflow-hidden relative">
      <Header />
      
      <main className="absolute top-[104px] bottom-0 left-0 right-0 overflow-hidden w-full" onContextMenu={(e) => e.preventDefault()}>
        {/* Interactive 3D Canvas */}
        <ViewerCanvas
          ref={canvasRef}
          modelUrl={assembly.product?.modelUrl}
          isChunked={!!(assembly.product?.modelUrl?.includes('manifest') || assembly.product?.modelUrl?.includes('.json'))}
          chunkProgress={chunkProgress}
          setChunkProgress={setChunkProgress}
          selectedComponent={selectedComponent}
          onComponentClick={handleComponentSelect}
          lighting={lighting}
          setLighting={setLighting}
          diagnosticRecords={diagnosticMap}
          componentWraps={showBeforeWrap ? componentWrapsBefore : componentWraps}
          viewMode={viewMode}
          showGrid={showGrid}
          isPlaying={isPlaying}
          isExploded={isExploded}
          explodeScaleX={explodeScaleX}
          explodeScaleY={explodeScaleY}
          explodeScaleZ={explodeScaleZ}
          isInfiniteVertical={isInfiniteVertical}
          modelYShift={0.35}
        />

        {/* Left Sidebar Pane: Report Details and Invoice Ledgers */}
        {showModelInfo && (
          <div className="absolute top-4 left-4 z-30 w-[420px] bg-white/[0.93] backdrop-blur-md rounded-xl border border-border shadow-floating max-h-[calc(100vh-10rem)] overflow-hidden flex flex-col transition-all duration-200 text-left">
            {/* Header / Shop Branded banner with Quick Return */}
            <div className={`px-4 py-3 border-b border-border flex items-center justify-between ${mode === 'preview' ? 'bg-indigo-500/5' : 'bg-emerald-500/5'}`}>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => navigate('/user-account-dashboard?tab=garage')}
                  className="text-xs text-muted-foreground hover:text-foreground font-bold p-1 rounded hover:bg-black/5 flex items-center gap-1"
                  title="Return to My Garage Services"
                >
                  <Icon name="ArrowLeft" size={12} />
                  <span>Garage</span>
                </button>
                <span className="text-border">|</span>
                <span className={`text-[10px] font-black uppercase tracking-widest flex items-center gap-1 ${mode === 'preview' ? 'text-indigo-600' : 'text-emerald-600'}`}>
                  <span className={`w-2 h-2 rounded-full animate-ping ${mode === 'preview' ? 'bg-indigo-500' : 'bg-emerald-500'}`} />
                  {mode === 'preview' ? 'Wrap Design Preview' : 'Diagnostic Report'}
                </span>
              </div>
              <Icon name={mode === 'preview' ? 'Palette' : 'Award'} className={mode === 'preview' ? 'text-indigo-500' : 'text-emerald-500'} size={16} />
            </div>

            {/* Asset and customer info */}
            <div className="p-4 border-b border-border space-y-2">
              <h3 className="text-lg font-black text-foreground uppercase tracking-wide truncate">
                {assembly.product?.name}
              </h3>
              {assembly.serial_number && (
                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">
                  SN / TAG: {assembly.serial_number}
                </p>
              )}
              {assembly.notes && (
                <p className="text-xs text-slate-500 italic leading-snug mt-1">
                  "{assembly.notes}"
                </p>
              )}
              
              <div className="pt-2 border-t border-border/40 text-xs text-muted-foreground space-y-1">
                <p><strong>Owner:</strong> {assembly.customer?.name}</p>
                {shopAccount && (
                  <p><strong>Serviced by:</strong> {shopAccount.business_name}</p>
                )}
              </div>
            </div>

            {/* Grouped Invoice repair logs list / Livery Details */}
            <div className="flex-1 overflow-y-auto min-h-0 p-4 space-y-4">
              {mode === 'preview' ? (
                <div className="space-y-4 text-left">
                  <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5">
                    <Icon name="Palette" size={14} />
                    Wrap Specifications
                  </h4>
                  
                  <div className="bg-indigo-500/10 border border-indigo-500/20 rounded-xl p-4 flex gap-3.5 items-center">
                    <div className="w-10 h-10 rounded-lg bg-indigo-500/20 text-indigo-600 flex items-center justify-center shrink-0">
                      <Icon name="Sparkles" size={20} />
                    </div>
                    <div>
                      <h4 className="text-xs font-black uppercase text-indigo-700 dark:text-indigo-400">
                        {assembly.nickname || 'Custom Wrap Design'}
                      </h4>
                      <p className="text-[10px] text-muted-foreground mt-0.5">
                        Interactive 3D wrap model design live preview.
                      </p>
                    </div>
                  </div>

                  {/* Before/After View Saved Project Toggle Button */}
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowBeforeWrap(!showBeforeWrap)}
                      className={`w-full py-2.5 px-4 rounded-xl font-black uppercase text-[10px] tracking-wider transition-all border flex items-center justify-center gap-2 shadow-sm cursor-pointer ${
                        showBeforeWrap 
                          ? 'bg-amber-100 border-amber-300 text-amber-700 hover:bg-amber-200' 
                          : 'bg-indigo-600 border-indigo-700 text-white hover:bg-indigo-500'
                      }`}
                      title={showBeforeWrap ? "Switch to After View (Wrapped)" : "Switch to Before View (Unwrapped)"}
                    >
                      <Icon name={showBeforeWrap ? "Eye" : "EyeOff"} size={12} />
                      <span>{showBeforeWrap ? 'Before View (Original)' : 'After View (Wrapped)'}</span>
                    </button>
                  </div>

                  <div className="space-y-2">
                    <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">
                      Customized Component Layers ({showBeforeWrap ? 'Before' : 'After'})
                    </span>
                    <div className="divide-y divide-border/60">
                      {Object.entries(showBeforeWrap ? componentWrapsBefore : componentWraps)
                        .filter(([compId, wrap]) => compId !== 'components' && compId !== 'product' && compId !== 'id' && compId !== 'shop_id' && compId !== 'assembly_id' && compId !== 'config_name' && typeof wrap === 'object' && wrap !== null && !Array.isArray(wrap))
                        .map(([compId, wrap]) => {
                        const compObj = assembly.product?.components?.find(c => c.id === compId || c.dbComponentId === compId);
                        const compName = wrap.componentName || compObj?.name || 'Body Panel / Layer';
                        const isLayerSelected = selectedComponent?.id === compId;
                        return (
                          <div 
                            key={compId} 
                            onClick={() => {
                              if (isLayerSelected) {
                                setSelectedComponent(null);
                              } else if (compObj) {
                                setSelectedComponent(compObj);
                              } else {
                                setSelectedComponent({ id: compId, name: compName });
                              }
                            }}
                            className={`py-2.5 px-3 rounded-lg flex items-center justify-between gap-3 text-xs cursor-pointer transition-colors border ${
                              isLayerSelected 
                                ? 'border-indigo-500 bg-indigo-500/5 text-indigo-700 font-extrabold shadow-sm' 
                                : 'border-transparent hover:bg-slate-50 text-foreground'
                            }`}
                          >
                            <span className="font-bold truncate max-w-[65%]">• {compName}</span>
                            <div className="flex items-center gap-2 shrink-0">
                              {wrap.color && (
                                <span 
                                  className="w-4 h-4 rounded-full border border-border shadow-sm shrink-0" 
                                  style={{ backgroundColor: wrap.color }}
                                  title={`Color: ${wrap.color}`}
                                />
                              )}
                              {wrap.decalUrl && (
                                <span className="text-[9px] font-black uppercase bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded" title="Custom Logo decal applied">
                                  Decal
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                      {Object.keys(componentWraps).length === 0 && (
                        <p className="text-xs text-muted-foreground italic text-center py-4">
                          No custom component layers adjusted yet.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-bold text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                      <Icon name="Receipt" size={14} />
                      Work Orders & Invoices
                    </h4>
                    {searchParams.get('invoiceNo') && (
                      <button
                        type="button"
                        onClick={() => {
                          const p = new URLSearchParams(window.location.search);
                          p.delete('invoiceNo');
                          navigate(`${window.location.pathname}?${p.toString()}`);
                        }}
                        className="text-[10px] text-indigo-600 dark:text-indigo-400 hover:underline font-bold"
                      >
                        Show All Invoices
                      </button>
                    )}
                  </div>

                  {searchParams.get('invoiceNo') && (
                    <div className="mb-3 p-2.5 bg-slate-900 border border-slate-700 text-white rounded-lg flex items-center justify-between text-xs shadow-md">
                      <span className="font-extrabold text-white truncate text-xs flex items-center gap-1.5">
                        <Icon name="Filter" size={13} className="text-indigo-400 shrink-0" />
                        Filtered to: <span className="text-indigo-200 underline decoration-indigo-400 font-black">{searchParams.get('invoiceNo')}</span>
                      </span>
                      <span className="text-[10px] bg-indigo-600 text-white font-black px-2 py-0.5 rounded uppercase tracking-wider shrink-0 ml-2">
                        1 Invoice
                      </span>
                    </div>
                  )}

                  {Object.entries(groupedInvoices).map(([invoiceNum, records]) => {
                    const sumCost = records.filter(r => r.client_approved !== false).reduce((sum, r) => sum + parseFloat(r.cost || 0), 0);
                    return (
                      <div key={invoiceNum} className="border border-border rounded-lg overflow-hidden bg-muted/5 shadow-sm text-left animate-in fade-in duration-300">
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedComponent(null);
                          }}
                          className="w-full bg-slate-50 dark:bg-slate-900 border-b border-border p-2.5 flex justify-between items-center text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors text-left cursor-pointer"
                          title="Click to reset selection and highlight all invoice items simultaneously!"
                        >
                          <span className="text-slate-700 dark:text-slate-100 truncate max-w-[70%] flex items-center gap-1.5 font-extrabold">
                            <Icon name="Receipt" size={14} className="text-indigo-600 shrink-0 animate-pulse" />
                            {invoiceNum}
                          </span>
                          <span className="text-emerald-600 dark:text-emerald-400 font-extrabold shrink-0">${sumCost.toFixed(2)}</span>
                        </button>

                        <div className="p-2 space-y-2 bg-card">
                          {records.map(r => (
                            <div 
                              key={r.id} 
                              onClick={() => {
                                if (selectedComponent?.name === r.component_id) {
                                  setSelectedComponent(null);
                                } else {
                                  const mockComp = { id: r.id, name: r.component_id, dbComponentId: r.id };
                                  setSelectedComponent(mockComp);
                                }
                              }}
                              className={`p-2.5 rounded-xl border transition-all cursor-pointer text-left relative ${
                                selectedComponent?.name === r.component_id
                                  ? 'border-primary bg-primary/5 shadow-inner'
                                  : 'border-border hover:bg-muted/30'
                              } ${r.client_approved === false ? 'opacity-60 bg-slate-500/5 line-through' : ''}`}
                            >
                              <div className="flex justify-between items-center mb-1.5 gap-2">
                                <span className={`text-[8px] font-black uppercase px-1.5 py-0.5 rounded-full ${
                                  r.status === 'replaced' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/10' :
                                  r.status === 'repaired' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/10' :
                                  r.status === 'needs_repair' ? 'bg-red-500/10 text-red-500 border border-red-500/10 animate-pulse' :
                                  'bg-yellow-500/10 text-yellow-500'
                                }`}>
                                  {r.status.replace('_', ' ')}
                                </span>
                                
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleApproval(r.id, r.client_approved);
                                  }}
                                  className={`text-[8px] font-black uppercase px-2 py-0.5 rounded border transition-all shrink-0 no-underline ${
                                    r.client_approved !== false
                                      ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-500 hover:bg-emerald-500/15'
                                      : 'bg-slate-800 border-slate-700 text-slate-400 hover:bg-slate-800/80'
                                  }`}
                                >
                                  {r.client_approved !== false ? '✓ Approved' : 'Deferred'}
                                </button>
                              </div>
                              <div className="flex justify-between items-center text-xs mt-1">
                                <span className="font-extrabold text-foreground truncate max-w-[65%]">{r.component_id}</span>
                                <span className="font-black text-foreground shrink-0">${parseFloat(r.cost).toFixed(2)}</span>
                              </div>
                              {r.notes && <p className="text-[10px] text-muted-foreground leading-snug mt-1.5">{r.notes}</p>}
                              {r.resolution && (
                                <div className="mt-1 p-1 bg-emerald-500/5 border border-emerald-500/10 rounded text-[9px] text-emerald-600 leading-snug">
                                  <strong>Fix:</strong> {r.resolution}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                  {serviceRecords.length === 0 && (
                    <div className="text-center py-12 border border-dashed border-border rounded-lg bg-card/40">
                      <Icon name="Clock" size={24} className="mx-auto text-muted-foreground/30 mb-2" />
                      <p className="text-xs text-muted-foreground">No historical repairs logged yet.</p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Footer Contact & Secure checkout CTA */}
            <div className="p-4 border-t border-border bg-slate-50 dark:bg-slate-950 flex flex-col gap-2 flex-shrink-0">
              {mode !== 'preview' && serviceRecords.length > 0 && (
                totalInvoiceCost <= 0.00 ? (
                  <div className="w-full py-2.5 px-3 rounded-xl bg-emerald-500/10 border border-emerald-500/30 text-emerald-700 dark:text-emerald-400 font-extrabold text-xs text-center flex items-center justify-center gap-2 mb-1.5">
                    <Icon name="CheckCircle" size={15} className="text-emerald-600 shrink-0" />
                    <span>No Payment Required ($0.00)</span>
                  </div>
                ) : (
                  serviceRecords[0]?.status !== 'completed' && (
                    <Button 
                      variant="default" 
                      onClick={() => setShowPaymentModal(true)}
                      className="w-full text-xs font-black uppercase tracking-wider h-11 bg-indigo-600 hover:bg-indigo-500 text-white shadow-md mb-1.5 flex items-center justify-center gap-1.5"
                    >
                      <Icon name="CreditCard" size={14} /> Click to Pay Invoice (${totalInvoiceCost.toFixed(2)})
                    </Button>
                  )
                )
              )}
              {shopAccount ? (
                <Button 
                  variant="outline" 
                  onClick={() => {
                    const mailSubject = encodeURIComponent(`Inquiry on 3D Diagnostic Report - SN: ${assembly.serial_number || 'N/A'}`);
                    window.open(`mailto:${assembly.customer?.email || ''}?subject=${mailSubject}`, '_blank');
                  }}
                  className="w-full text-xs font-black uppercase tracking-wider h-10"
                >
                  <Icon name="Mail" size={14} className="mr-1.5" /> Email Shop Support
                </Button>
              ) : (
                <Button variant="default" onClick={() => navigate('/model-catalog')} className="w-full text-xs font-black uppercase tracking-wider">
                  Browse Catalog
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Symmetrical Rich Right-Hand Control Overlays */}
        {showModelInfo && (
          <ViewerControls
            isFullscreen={isFullscreen}
            onToggleFullscreen={() => setIsFullscreen(p => !p)}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            isPlaying={isPlaying}
            onToggleAnimation={() => setIsPlaying(p => !p)}
            onScreenshot={handleScreenshot}
            onShare={handleShare}
            onReset={handleReset}
            showGrid={showGrid}
            onToggleGrid={() => setShowGrid(p => !p)}
            isInfiniteVertical={isInfiniteVertical}
            onToggleInfiniteVertical={() => setIsInfiniteVertical(p => !p)}
            showLighting={showLighting}
            onToggleLighting={() => setShowLighting(p => !p)}
            materialColor="#ffffff"
            onMaterialColorChange={handleMaterialColorChange}
            isExploded={isExploded}
            onToggleExplode={() => setIsExploded(p => !p)}
            explodeScaleX={explodeScaleX}
            explodeScaleY={explodeScaleY}
            explodeScaleZ={explodeScaleZ}
            onExplodeScaleXChange={handleScaleChangeX}
            onExplodeScaleYChange={handleScaleChangeY}
            onExplodeScaleZChange={handleScaleChangeZ}
            isExplodeLocked={isExplodeLocked}
            onToggleExplodeLock={handleToggleExplodeLock}
            isLoggedIn={!!user}
            onAuthRequired={() => {}}
          />
        )}

        {/* Floating Bottom Menu for panel toggles */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
          <div className="bg-white/[0.88] backdrop-blur-md rounded-lg border border-border shadow-floating p-1.5 flex gap-1">
            <Button variant={showModelInfo ? "default" : "ghost"} size="sm" iconName="Info" onClick={() => setShowModelInfo(p => !p)} title="Report Information" />
          </div>
        </div>
      </main>

      {/* Invoice Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
          <div className="bg-slate-950 border border-slate-800 shadow-2xl rounded-2xl w-full max-w-md p-6 relative text-center z-10 animate-in fade-in zoom-in-95 duration-200 text-slate-100">
            <button 
              onClick={() => setShowPaymentModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white transition-colors"
            >
              <Icon name="X" size={18} />
            </button>

            <div className="w-12 h-12 bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 rounded-xl flex items-center justify-center mx-auto mb-3">
              <Icon name="CreditCard" size={24} />
            </div>

            <h3 className="text-base font-black uppercase tracking-wider text-slate-100">
              Invoice Payment
            </h3>
            
            {/* Prominent Invoice Amount */}
            <div className="my-2">
              <div className="text-3xl font-black text-emerald-400 tracking-tight">
                ${totalInvoiceCost.toFixed(2)}
              </div>
              <p className="text-xs text-slate-400 mt-0.5">
                {searchParams.get('invoiceNo') ? `Invoice #${searchParams.get('invoiceNo')}` : 'Full Work Order Balance'}
              </p>
            </div>

            {/* Payment Method Selector */}
            <div className="space-y-2.5 my-5 text-left">
              <label className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">
                Select Payment Method:
              </label>

              {/* Option 1: Credit Card on File */}
              <div 
                onClick={() => setSelectedPaymentType('card_on_file')}
                className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                  selectedPaymentType === 'card_on_file' 
                    ? 'border-emerald-500 bg-emerald-500/10 text-white ring-1 ring-emerald-500' 
                    : 'border-slate-800 bg-slate-900/60 text-slate-300 hover:bg-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input 
                    type="radio" 
                    name="paymentChoice" 
                    checked={selectedPaymentType === 'card_on_file'} 
                    onChange={() => setSelectedPaymentType('card_on_file')} 
                    className="text-emerald-500 focus:ring-emerald-500"
                  />
                  <div>
                    <p className="text-xs font-bold flex items-center gap-1.5 text-slate-200">
                      <Icon name="CreditCard" size={14} className="text-emerald-400" />
                      Credit Card on File
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {userPaymentMethods[0] 
                        ? `${userPaymentMethods[0].cardBrand || 'Card'} ending in •••• ${userPaymentMethods[0].cardLastFour || '4242'}`
                        : 'Default account card on file'}
                    </p>
                  </div>
                </div>
                <span className="text-[9px] bg-emerald-500/20 text-emerald-300 px-2 py-0.5 rounded font-black uppercase">
                  Primary
                </span>
              </div>

              {/* Option 2: Card assigned to invoice / customer profile */}
              <div 
                onClick={() => setSelectedPaymentType('invoice_card')}
                className={`p-3 rounded-xl border cursor-pointer transition-all flex items-center justify-between ${
                  selectedPaymentType === 'invoice_card' 
                    ? 'border-emerald-500 bg-emerald-500/10 text-white ring-1 ring-emerald-500' 
                    : 'border-slate-800 bg-slate-900/60 text-slate-300 hover:bg-slate-900'
                }`}
              >
                <div className="flex items-center gap-3">
                  <input 
                    type="radio" 
                    name="paymentChoice" 
                    checked={selectedPaymentType === 'invoice_card'} 
                    onChange={() => setSelectedPaymentType('invoice_card')} 
                    className="text-emerald-500 focus:ring-emerald-500"
                  />
                  <div>
                    <p className="text-xs font-bold flex items-center gap-1.5 text-slate-200">
                      <Icon name="Receipt" size={14} className="text-indigo-400" />
                      Card Assigned to Invoice
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {assembly.customer?.payment_method 
                        ? `${assembly.customer.payment_method.brand || 'Card'} ending in •••• ${assembly.customer.payment_method.last4 || '••••'}`
                        : 'Direct card authorized for this invoice work order'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
                className="flex-1 text-xs"
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="default"
                disabled={paying}
                onClick={handlePayInvoice}
                className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-black uppercase tracking-wider py-2.5 h-auto shadow-md"
              >
                {paying ? 'Processing Payment...' : `Pay $${totalInvoiceCost.toFixed(2)}`}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
